import React, { type ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Alert,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { uploadFile } from '~api/services/utils/upload_utils'
import { isErrorResponse } from '~api/services/utils/utils'
import { DocumentIcon, PreviewIcon, UploadIcon } from '~assets/icons'
import { Icon, IconButton } from '~components/internal'
import { SECURE_CLOUDFRONT_DOMAIN } from '~config'
import { type RootState } from '~redux/rootReducer'
import {
  type AssetType,
  type Attachment,
  DEFAULT_FILE_TYPE_WHITELIST,
  type IdentityId,
  s3securebase,
  s3sha256base,
} from '~types'
import { useDragAndDrop } from '~utils/fileUtils'

export const trimFileName = (fileName: string): string => {
  return fileName.length > 50 ? fileName.substring(0, 50) + '...' : fileName
}

export const getFileIcon = (fileName: string): ReactElement => {
  const extension = fileName.split('.').pop()?.toLowerCase()
  if (['jpeg', 'jpg', 'png'].includes(extension || '')) {
    return <Icon name='image' fontSize='small' />
  }
  return <DocumentIcon />
}

interface FileUploaderProps {
  onFilesChanged: (files: Attachment[]) => void
  owner: IdentityId
  uploadType: AssetType
  caseHandle?: string
  caseId?: string
  initialFiles?: Attachment[]
  disabled?: boolean
  fileDescription?: string
  selectMultiple?: boolean
  uploaderId?: string
  secure?: boolean
  allowedFileTypes?: string[]
  error?: boolean
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onFilesChanged,
  owner,
  uploadType,
  caseHandle = null,
  caseId = null,
  initialFiles = [],
  disabled = false,
  fileDescription,
  selectMultiple = true,
  secure = true,
  uploaderId,
  allowedFileTypes = DEFAULT_FILE_TYPE_WHITELIST,
  error,
}) => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const [files, setFiles] = useState<Attachment[]>([])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null)
  const viewButtonRefs = useRef<(HTMLButtonElement | null)[]>([])
  const theme = useTheme()
  const uploadFilesRef = useRef<(selectedFiles: FileList) => Promise<void>>(async () => {})
  const prevInitialFilesRef = useRef<string>('')

  const { isDragging, handleDragEnter, handleDragLeave, handleDragOver, handleDrop } =
    useDragAndDrop(uploadFilesRef, disabled)

  useEffect(() => {
    const serialized = JSON.stringify(initialFiles)
    if (serialized === prevInitialFilesRef.current) return
    if (uploading) return
    prevInitialFilesRef.current = serialized
    setFiles(initialFiles)
  }, [initialFiles, uploading])

  const urlToName = (url: string): string => {
    const decodedUrl = decodeURIComponent(url)
    return decodedUrl.split('/').pop() || ''
  }

  const validateFileType = (file: File | undefined): boolean => {
    if (!allowedFileTypes || allowedFileTypes.length === 0 || !file) {
      return true
    }

    const fileName = file.name.toLowerCase()
    const fileType = file.type.toLowerCase()

    return allowedFileTypes.some((allowedType) => {
      const lowerAllowedType = allowedType.toLowerCase()

      if (lowerAllowedType.startsWith('.')) {
        return fileName.endsWith(lowerAllowedType)
      }

      if (lowerAllowedType.includes('/')) {
        return fileType === lowerAllowedType
      }

      return fileType.startsWith(lowerAllowedType + '/')
    })
  }

  const partitionFilesByValidity = (files: FileList): [FileList, FileList] => {
    // Returns a tuple of two FileLists: [validFiles, invalidFiles]
    if (!allowedFileTypes || allowedFileTypes.length === 0) {
      return [files, new DataTransfer().files]
    }

    const validData = new DataTransfer()
    const invalidData = new DataTransfer()
    Array.from(files).forEach((file) => {
      if (validateFileType(file)) {
        validData.items.add(file)
      } else {
        invalidData.items.add(file)
      }
    })

    return [validData.files, invalidData.files]
  }

  const getValidUniqueFiles = (newFiles: FileList): File[] => {
    setValidationErrors(null)
    const [validFiles, invalidFiles] = partitionFilesByValidity(newFiles)
    const uploadErrorMessages: string[] = []
    if (invalidFiles.length > 0) {
      const invalidNames = Array.from(invalidFiles)
        .map((file) => file.name)
        .join(', ')
      uploadErrorMessages.push(
        `The following files have invalid types and were not uploaded: ${invalidNames}`,
      )
      if (validFiles.length === 0) {
        setValidationErrors(uploadErrorMessages)
        return []
      }
    }

    const validUniqueFiles: File[] = []
    const duplicateNames: string[] = []
    Array.from(validFiles).forEach((file) => {
      if (
        !files.some((existingFile) => existingFile.name === file.name) &&
        !validUniqueFiles.some((existingFile) => existingFile.name === file.name)
      ) {
        validUniqueFiles.push(file)
      } else {
        duplicateNames.push(file.name)
      }
    })
    if (duplicateNames.length > 0) {
      uploadErrorMessages.push(
        `Some files were already uploaded and have been skipped: ${duplicateNames.join(', ')}`,
      )
      if (validUniqueFiles.length === 0) {
        setValidationErrors(uploadErrorMessages)
        return []
      }
    }
    setValidationErrors(uploadErrorMessages.length > 0 ? uploadErrorMessages : null)
    return validUniqueFiles
  }

  const uploadFiles = async (selectedFiles: FileList): Promise<void> => {
    if (!userId) {
      return
    }

    const validUniqueFiles = getValidUniqueFiles(selectedFiles)
    if (validUniqueFiles.length === 0) {
      return
    }
    setUploading(true)
    try {
      const uploadedFiles: Attachment[] = []
      for (const validFile of validUniqueFiles) {
        const response = await uploadFile(
          userId,
          owner,
          validFile,
          uploadType,
          caseHandle,
          caseId,
          secure,
        )

        if (response && response.url) {
          uploadedFiles.push({
            name: urlToName(response.url),
            url: response.url,
            access_url: response.accessible_url,
          })
        } else {
          console.error('Upload failed')
        }
      }
      const newFiles = selectMultiple ? [...files, ...uploadedFiles] : uploadedFiles
      const indexToFocus = selectMultiple ? files.length : 0

      setFiles(newFiles)
      onFilesChanged(newFiles)

      if (uploadedFiles.length > 0) {
        setTimeout(() => {
          const buttonToFocus = viewButtonRefs.current[indexToFocus]
          if (buttonToFocus) {
            buttonToFocus.focus()
          }
        }, 300)
      }
    } catch (error) {
      if (isErrorResponse(error)) {
        console.error('Error during upload:', error.message)
      } else {
        console.error('Unknown error during upload:', error)
      }
    } finally {
      setUploading(false)
    }
  }

  uploadFilesRef.current = uploadFiles

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        uploadFiles(event.target.files)
      }
    },
    [uploadFiles],
  )

  const handleRemoveFile = (fileToRemove: Attachment): void => {
    const updatedFiles = files.filter((file) => file !== fileToRemove)
    setFiles(updatedFiles)
    onFilesChanged(updatedFiles)
    // Reset the file input value so the same file can be re-uploaded
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleViewFile = (file: Attachment): void => {
    if (file.access_url) {
      window.open(file.access_url, '_blank')
      return
    }
    if (file.url.startsWith(s3securebase) || file.url.startsWith(SECURE_CLOUDFRONT_DOMAIN)) {
      window.open(file.url, '_blank')
      return
    }
    const newUrl = file.url.startsWith(s3sha256base) ? file.url : `${s3sha256base}${file.url}`
    window.open(newUrl, '_blank')
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {uploading && <LinearProgress sx={{ width: '100%', p: 0.5, mb: 1 }} />}
      {!disabled && !uploading && (
        <>
          <input
            ref={fileInputRef}
            accept={allowedFileTypes.join(',')}
            id={`file-upload-${uploaderId}`}
            type='file'
            multiple={selectMultiple}
            disabled={disabled}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <label htmlFor={`file-upload-${uploaderId}`}>
            <Box
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              tabIndex={0}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') &&
                !disabled &&
                document.getElementById(`file-upload-${uploaderId}`)?.click()
              }
              sx={{
                border: `1px dashed ${
                  isDragging
                    ? theme.palette.primary.main
                    : error && !uploading
                      ? theme.palette.error.main
                      : theme.palette.primary.light
                }`,
                borderRadius: '4px',
                cursor: disabled ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100px',
                minWidth: '300px',
                backgroundColor: isDragging ? theme.palette.action.hover : 'white',
                '&:hover': {
                  border: `2px solid ${theme.palette.input.hover}`,
                  backgroundColor: disabled ? 'transparent' : theme.palette.action.hover,
                },
                '&:focus-visible': {
                  border: `2px solid ${theme.palette.input.hover}`,
                  boxShadow: `0 0 0 2px ${theme.palette.input.focus}`,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <UploadIcon color={theme.palette.primary.main} />
                <Typography sx={{ ml: 1, mt: 1 }}>
                  {`Attach File${selectMultiple ? '(s)' : ''}`}
                </Typography>
                {fileDescription && (
                  <Typography
                    variant='caption'
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {fileDescription}
                  </Typography>
                )}
              </Box>
            </Box>
          </label>
        </>
      )}
      {files.length > 0 && (
        <List sx={{ p: 0, mb: 2 }}>
          {files.map((file, index) => (
            <ListItem
              key={index}
              data-file-index={index}
              sx={{
                borderBottom: '1px solid #e0e0e0',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getFileIcon(file.name)}
                    <Typography
                      noWrap
                      sx={{
                        flexGrow: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        paddingRight: '60px',
                      }}
                    >
                      {trimFileName(file.name)}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  ref={(buttonRef) => (viewButtonRefs.current[index] = buttonRef)}
                  edge='end'
                  aria-label='view'
                  onClick={() => handleViewFile(file)}
                  size='small'
                  sx={{ mr: 1 }}
                >
                  <PreviewIcon />
                </IconButton>
                {!disabled && (
                  <IconButton
                    edge='end'
                    aria-label='delete'
                    onClick={() => handleRemoveFile(file)}
                    size='small'
                  >
                    <Icon name='close' fontSize='small' />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
      {validationErrors && (
        <Box>
          {validationErrors.map((errorText, index) => {
            return (
              <Alert key={index} severity='error' sx={{ mt: 1, width: '100%' }}>
                {errorText}
              </Alert>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default FileUploader
